import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Slider from '@material-ui/core/Slider';
import './SensitivitySelection.css';
import { IconSource } from '../../../../utils/imagesource';
import {
  setLayerTransparency,
  setSuitableFilter,
  setSensitivityRanks,
  setStateSensitivityRanks,
} from '../../../../redux/reducers/map';
import Toggle from '../../../ui/shared/Toggle';

//

const useStyles = makeStyles((props) => {
  return {
    color: {
      backgroundColor: (props) => `${props.color}`,
      borderRadius: '8px 0px 0px 8px',
    },
  };
});

const SensitivityCard = (props) => {
  const mapState = useSelector((state) => state.map.mapState);
  const mapStateRef = useRef({});
  const ranks = { 1: 'Low', 2: 'Moderate', 3: 'High', 4: 'Very High' };
  const ranksAUS = {
    11: 'Low',
    12: 'Low-Moderate',
    21: 'Moderate',
    22: 'Moderate-High',
    31: 'High',
    32: 'High-Very High',
    41: 'Very High',
    42: 'Extremely High',
  };
  let [rankLabel, setRankLabel] = useState('');
  const color = props.color;
  const classes = useStyles({ color });

  const toggleRank = (checked) => {
    props.onToggle({ rank: props.rank, checked: checked });
  };

  useEffect(() => {
    mapStateRef.current = mapState;
    let tempRank =
      (mapStateRef?.current?.country === 'AUS' &&
        mapStateRef?.current?.region !== null) ||
      (mapStateRef?.current?.country === 'AUS' &&
        mapStateRef?.current?.energy?.code === 'OFFSHORE')
        ? ranksAUS[props.rank]
        : ranks[props.rank];
    setRankLabel(tempRank);
    //console.log("ranklabel", rankLabel);
  }, [mapState]);

  return (
    <div className='sensitivity-card'>
      <div className={classes.color}></div>
      <div className='labels'>
        <div className='label1'>{rankLabel}</div>
        {props.showAreas && (
          <div className='label2'>
            {props.stats?.area.toLocaleString('en-US')} km<sup>2</sup>
          </div>
        )}
      </div>
      <div className='percent'>
        {props.showAreas && <div>{props.stats?.percentage}%</div>}
      </div>
      <div className='toggle'>
        <Toggle enabled={props.toggledState} onClick={toggleRank} />
      </div>
    </div>
  );
};

const SensitivitySelection = (props) => {
  const config = useSelector((state) => state.config);
  const mapState = useSelector((state) => state.map.mapState);
  const transparency = useSelector((state) => state.map.transparency);
  const suitable = useSelector((state) => state.map.suitable);
  const sensitivity_ranks = useSelector((state) => state.map.sensitivity_ranks);
  const state_sensitivity_ranks = useSelector(
    (state) => state.map.state_sensitivity_ranks
  );
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rankAreas = useSelector((state) => state.map.rankAreas);

  const iconref = IconSource['NAVBACK'] || null;

  let [currentEnergy, setCurrentEnergy] = useState(null);
  let [suitableToggle, setSuitableToggle] = useState(false);
  let [suitableOnly, setSuitableOnly] = useState(false);
  let [visibleRanks, setVisibleRanks] = useState(
    (mapState.country === 'AUS' && mapState.region !== null) ||
      (mapState?.country === 'AUS' && mapState?.energy?.code === 'OFFSHORE')
      ? state_sensitivity_ranks
      : sensitivity_ranks
  );
  let [rankStats, setRankStats] = useState({ ...rankAreas });
  let [areasFlag, setAreasFlag] = useState(false);

  //TODO transparency should be tied to layer transparency
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mapStateRef = useRef({});
  let [transparent, setTransparency] = useState(transparency);
  const togDef = true;

  useEffect(() => {
    setRankStats(rankAreas);
    if (
      (mapState.country === 'AUS' && mapState.region !== null) ||
      (mapState.country === 'AUS' && mapState?.energy?.code === 'OFFSHORE')
    ) {
      setVisibleRanks(state_sensitivity_ranks);
    } else {
      setVisibleRanks(sensitivity_ranks);
    }
  }, [rankAreas]);

  useEffect(() => {
    mapStateRef.current = mapState;
    if (mapState.energy != currentEnergy) {
      setCurrentEnergy(mapState.energy);
    }
    let showAreas = !mapState.lat ? true : false;
    setAreasFlag(showAreas);

    if (
      (mapState.country === 'AUS' && mapState.region !== null) ||
      (mapState.country === 'AUS' && mapState?.energy?.code === 'OFFSHORE')
    ) {
      setVisibleRanks(state_sensitivity_ranks);
    } else {
      setVisibleRanks(sensitivity_ranks);
    }
  }, [mapState, currentEnergy]);

  useEffect(() => {
    if (currentEnergy && ['ONSHORE', 'OFFSHORE'].includes(currentEnergy.code)) {
      setSuitableToggle('Suitable Wind Speed Areas Only');
    } else if (currentEnergy && ['SOLAR'].includes(currentEnergy.code)) {
      setSuitableToggle(
        'Suitable Global Horizontal Irradiance (GHI) Areas Only'
      );
    } else {
      setSuitableToggle(false);
    }
  }, [currentEnergy]);

  const navBack = () => {
    let param = Object.fromEntries([...new URLSearchParams(location.search)]);
    delete param['energy'];
    if (mapStateRef.current.lat) {
      param['lat'] = mapStateRef.current.lat;
      param['long'] = mapStateRef.current.long;
    }
    const params = new URLSearchParams(param);
    navigate({
      pathname: location.pathname,
      search: params.toString(),
      replace: true,
    });
  };

  const transparencyChange = (event, newValue) => {
    setTransparency(newValue);
    dispatch(setLayerTransparency(newValue));
  };

  const toggleSuitable = (checked) => {
    setSuitableOnly(checked);
    dispatch(setSuitableFilter(checked));
  };

  const toggleSensitivity = (status) => {
    let ranks = { ...visibleRanks };
    ranks[status.rank] = status.checked;
    setVisibleRanks(ranks);
    /* if (mapState.country === 'AUS' && mapState.region !== null) {
      setVisibleRanks(state_sensitivity_ranks);
    } else {
      setVisibleRanks(sensitivity_ranks);
    } */
    dispatch(setSensitivityRanks(ranks));
    dispatch(setStateSensitivityRanks(ranks));
  };

  return (
    <div className='sensitivity-root'>
      <div className='header'>
        <div className='backnav' onClick={navBack}>
          <img src={iconref} />
        </div>
        <div className='title'>
          <div className='title1'>Energy Type</div>
          {currentEnergy && <div className='title2'>{currentEnergy.label}</div>}
        </div>
      </div>
      <div className='desc bodytext3'>
        <div>Avian Sensitivity</div>
      </div>
      <div className='cards'>
        {(mapStateRef.current.country === 'AUS' &&
          mapStateRef.current.region !== null) ||
        (mapState.country === 'AUS' &&
          mapState?.energy?.code === 'OFFSHORE') ? (
          <>
            <SensitivityCard
              rank='11'
              key='11'
              color='#27DB85FF'
              showAreas={areasFlag}
              stats={
                rankStats['11'] ? rankStats['11'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['11']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='12'
              key='12'
              color='#A6F5C1FF'
              showAreas={areasFlag}
              stats={
                rankStats['12'] ? rankStats['12'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['12']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='21'
              key='21'
              color='#F5FF93FF'
              showAreas={areasFlag}
              stats={
                rankStats['21'] ? rankStats['21'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['21']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='22'
              key='22'
              color='#FFEE15FF'
              showAreas={areasFlag}
              stats={
                rankStats['22'] ? rankStats['22'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['22']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='31'
              key='31'
              color='#FFC465FF'
              showAreas={areasFlag}
              stats={
                rankStats['31'] ? rankStats['31'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['31']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='32'
              key='32'
              color='#FF8805FF'
              showAreas={areasFlag}
              stats={
                rankStats['32'] ? rankStats['32'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['32']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='41'
              key='41'
              color='#EB4949FF'
              showAreas={areasFlag}
              stats={
                rankStats['41'] ? rankStats['41'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['41']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='42'
              key='42'
              color='#C40000FF'
              showAreas={areasFlag}
              stats={
                rankStats['42'] ? rankStats['42'] : { area: 0, percentage: 0 }
              }
              toggledState={visibleRanks['42']}
              onToggle={toggleSensitivity}
            />
          </>
        ) : (
          <>
            <SensitivityCard
              rank='1'
              key='1'
              color='#55FF00'
              showAreas={areasFlag}
              stats={rankStats['1']}
              toggledState={visibleRanks['1']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='2'
              key='2'
              color='#FFFF00'
              showAreas={areasFlag}
              stats={rankStats['2']}
              toggledState={visibleRanks['2']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='3'
              key='3'
              color='#FFAA00'
              showAreas={areasFlag}
              stats={rankStats['3']}
              toggledState={visibleRanks['3']}
              onToggle={toggleSensitivity}
            />
            <SensitivityCard
              rank='4'
              key='4'
              color='#FF0000'
              showAreas={areasFlag}
              stats={rankStats['4']}
              toggledState={visibleRanks['4']}
              onToggle={toggleSensitivity}
            />
          </>
        )}
      </div>
      <div className='suitable'>
        {suitableToggle && <div>{suitableToggle}</div>}
        {suitableToggle && (
          <Toggle enabled={suitableOnly} onClick={toggleSuitable} />
        )}
      </div>
      <div className='transparency bodytext3'>
        <div>Sensitivity Layer Transparency</div>
        <Slider
          className='slider'
          value={transparent}
          onChange={transparencyChange}
          aria-labelledby='Layer Transparency'
        />
      </div>
    </div>
  );
};

export default SensitivitySelection;
